import { ask, IAskError } from '@monorepo/tools/src/lib/tools/ask/ask';
import { isAbortError } from '@monorepo/tools/src/lib/tools/ask/guards';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import endpoints from './endpoints/index.endpoints';
import { IPublisherLink } from '../models/link.model';

export interface IGenerateLinkParams {
	accountId: id;
	campaignId: id;
	domain: string;
	geo: string;
	linkType: 'HP' | 'DL';
}

export interface IGenerateLinkResponse {
	trackingUrl: string;
	isDuplicate?: boolean;
}

/** Normalized API error after `ask` (body uses `message` from http-response middleware). */
export interface ILinksApiCallError {
	status?: number;
	message: string;
}

function normalizeAskHttpError(err: IAskError, fallbackMessage: string): ILinksApiCallError {
	const status = err.response?.status;
	/** `IAskError.data` is untyped; guard with typeof so non-objects do not throw. */
	const serverMessage = typeof err.data?.message === 'string' ? err.data.message.trim() : '';
	return {
		status,
		message:
			serverMessage ||
			(status === 400 ? 'We could not complete this request. Please check your selection and try again.' : fallbackMessage),
	};
}

function rejectUnlessAbort(err: IAskError, fallbackMessage: string): Promise<never> {
	if (isAbortError(err)) {
		return Promise.reject(err);
	}
	return Promise.reject(normalizeAskHttpError(err, fallbackMessage));
}

export const LinksApi = {
	generateLink: (params: IGenerateLinkParams): Promise<IGenerateLinkResponse> => {
		return ask
			.post(endpoints.links.generateLink(), params)
			.then(res => res?.payload)
			.catch((err: IAskError) => rejectUnlessAbort(err, 'An error occurred while generating the link.'));
	},

	myLinks: (params: { accountId: id }): Promise<IPublisherLink[]> => {
		return ask
			.post(endpoints.links.myLinks(params.accountId), {})
			.then(res => res?.payload?.list ?? res?.payload ?? [])
			.catch((err: IAskError) => rejectUnlessAbort(err, 'Failed to load links.'));
	},

	stopLink: (linkId: id, publisherId: id): Promise<void> => {
		return ask
			.post(endpoints.links.stopLink(linkId), { linkId, publisherId })
			.then(() => undefined)
			.catch((err: IAskError) => rejectUnlessAbort(err, 'An error occurred while stopping the link.'));
	},

	regenerateLink: (linkId: id, publisherId: id): Promise<IGenerateLinkResponse> => {
		return ask
			.post(endpoints.links.regenerateLink(linkId), { linkId, publisherId })
			.then(res => res?.payload)
			.catch((err: IAskError) => rejectUnlessAbort(err, 'An error occurred while regenerating the link.'));
	},
};
